import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  Box,
  CircularProgress,
  Drawer,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { colors } from "../constants/designTokens";
import { navItems } from "../data/mockData";
import PageHeader from "./PageHeader";
import AgentePanel, { AgenteFab } from "./AgentePanel";
import { useTokenValidation, TokenExpiredDialog } from "../../../utils/tokenValidation";

const AGENTE_ENABLED_ROUTES = new Set([
  "/agentecontabilidade/visao-geral",
  "/agentecontabilidade/dre-viabilidade",
  "/agentecontabilidade/vendas",
  "/agentecontabilidade/carteira-recebiveis",
  "/agentecontabilidade/custos",
  "/agentecontabilidade/impostos",
  "/agentecontabilidade/despesas",
]);

const AGENTE_WIDGET_MARGIN = 28;
let agenteWidgetPosition = null;
let agenteOpenState = false;
const agenteOpenListeners = new Set();

function shouldIgnoreOverlayClose(reason) {
  return reason === "backdropClick" || reason === "escapeKeyDown";
}

function subscribeAgenteOpen(listener) {
  agenteOpenListeners.add(listener);
  return () => agenteOpenListeners.delete(listener);
}

function getAgenteOpenSnapshot() {
  return agenteOpenState;
}

function setAgenteOpenState(nextOpen) {
  if (agenteOpenState === nextOpen) {
    return;
  }

  agenteOpenState = nextOpen;
  agenteOpenListeners.forEach((listener) => listener());
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export default function AgenteContabilidadeShell({
  title,
  subtitle,
  actions,
  agenteContext,
  updating = false,
  children,
}) {
  const aiOpen = useSyncExternalStore(
    subscribeAgenteOpen,
    getAgenteOpenSnapshot,
    getAgenteOpenSnapshot
  );
  const widgetRef = useRef(null);
  const dragRef = useRef(null);
  const [widgetPosition, setWidgetPosition] = useState(agenteWidgetPosition);
  const [isDraggingAgente, setIsDraggingAgente] = useState(false);
  const [showUpdating, setShowUpdating] = useState(false);
  const { showExpiredDialog, countdown, redirectToHome } = useTokenValidation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const shouldShowAgente = AGENTE_ENABLED_ROUTES.has(location.pathname);
  const shouldReserveFabSpace = shouldShowAgente && !aiOpen;
  const currentTabIndex = Math.max(
    navItems.findIndex((item) => item.to === location.pathname),
    0
  );

  useEffect(() => {
    if (!updating) {
      setShowUpdating(false);
      return undefined;
    }

    const timer = window.setTimeout(() => setShowUpdating(true), 180);
    return () => window.clearTimeout(timer);
  }, [updating]);

  const handleTabChange = (_, newIndex) => {
    const selectedItem = navItems[newIndex];
    if (selectedItem?.to) {
      navigate(selectedItem.to);
    }
  };

  const handleAgenteDragStart = useCallback(
    (event) => {
      if (!isDesktop || event.button !== 0) {
        return;
      }

      if (event.target.closest("button,input,textarea,a")) {
        return;
      }

      const rect = widgetRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      event.preventDefault();
      dragRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        startLeft: rect.left,
        startTop: rect.top,
        width: rect.width,
        height: rect.height,
      };
      setIsDraggingAgente(true);
    },
    [isDesktop]
  );

  useEffect(() => {
    if (!isDraggingAgente) {
      return undefined;
    }

    function handlePointerMove(event) {
      const drag = dragRef.current;
      if (!drag) {
        return;
      }

      const maxLeft = window.innerWidth - drag.width - AGENTE_WIDGET_MARGIN;
      const maxTop = window.innerHeight - drag.height - AGENTE_WIDGET_MARGIN;
      const nextPosition = {
        left: clamp(
          drag.startLeft + event.clientX - drag.startX,
          AGENTE_WIDGET_MARGIN,
          Math.max(AGENTE_WIDGET_MARGIN, maxLeft)
        ),
        top: clamp(
          drag.startTop + event.clientY - drag.startY,
          AGENTE_WIDGET_MARGIN,
          Math.max(AGENTE_WIDGET_MARGIN, maxTop)
        ),
      };

      agenteWidgetPosition = nextPosition;
      setWidgetPosition(nextPosition);
    }

    function handlePointerUp() {
      dragRef.current = null;
      setIsDraggingAgente(false);
    }

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("blur", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("blur", handlePointerUp);
    };
  }, [isDraggingAgente]);

  useEffect(() => {
    if (!widgetPosition) {
      return undefined;
    }

    function handleResize() {
      const rect = widgetRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      const nextPosition = {
        left: clamp(
          widgetPosition.left,
          AGENTE_WIDGET_MARGIN,
          Math.max(AGENTE_WIDGET_MARGIN, window.innerWidth - rect.width - AGENTE_WIDGET_MARGIN)
        ),
        top: clamp(
          widgetPosition.top,
          AGENTE_WIDGET_MARGIN,
          Math.max(AGENTE_WIDGET_MARGIN, window.innerHeight - rect.height - AGENTE_WIDGET_MARGIN)
        ),
      };

      agenteWidgetPosition = nextPosition;
      setWidgetPosition(nextPosition);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [widgetPosition]);

  return (
    <Box
      sx={{
        mx: { xs: -3, md: -3 },
        my: -3,
        height: { xs: "calc(100dvh - 64px)", md: "calc(100vh - 64px)" },
        "--ac-page": isDarkMode ? "#070d1a" : "#f3f7fb",
        "--ac-surface": isDarkMode ? "#0f172a" : "#ffffff",
        "--ac-surface-alt": isDarkMode ? "#111c31" : "#f8fbff",
        "--ac-surface-soft": isDarkMode ? "#17243a" : "#eef3f9",
        "--ac-border": isDarkMode ? "rgba(148, 163, 184, 0.22)" : "#dce6f2",
        "--ac-text": isDarkMode ? "#f8fafc" : "#001432",
        "--ac-muted": isDarkMode ? "#cbd5e1" : "#526783",
        "--ac-soft-muted": isDarkMode ? "#94a3b8" : "#8fa0bb",
        "--ac-highlight-emerald": isDarkMode ? "rgba(16, 185, 129, 0.14)" : "#e7fbf2",
        "--ac-highlight-blue": isDarkMode ? "rgba(59, 130, 246, 0.14)" : "#eef6ff",
        "--ac-highlight-amber": isDarkMode ? "rgba(245, 158, 11, 0.14)" : "#fff4df",
        "--ac-highlight-purple": isDarkMode ? "rgba(168, 85, 247, 0.14)" : "#f4eaff",
        "--ac-editable-bg": isDarkMode ? "rgba(234, 179, 8, 0.16)" : "#fff5c7",
        bgcolor: colors.page,
        display: "flex",
        overflow: "hidden",
        borderRadius: { xs: 0, md: 2 },
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <Box
            sx={{
              px: { xs: 2, md: 5 },
              pt: { xs: 2.5, md: 5 },
              pb: shouldReserveFabSpace ? { xs: 13.5, md: 13.5 } : { xs: 2.5, md: 5 },
              scrollPaddingBottom: shouldReserveFabSpace ? { xs: 108, md: 108 } : 0,
            }}
          >
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: { xs: 3, md: 4 },
                px: { xs: 2, sm: 2.5, md: 3 },
                py: { xs: 1.6, md: 2.2 },
                mb: { xs: 1.5, md: 2 },
                color: "#fff",
              }}
            >
              <Box
                component="h1"
                sx={{
                  m: 0,
                  fontSize: { xs: "1rem", sm: "1.125rem", md: "1.15rem" },
                  fontWeight: 700,
                  lineHeight: 1.1,
                }}
              >
                Contabilidade
              </Box>
            </Box>

            <Box
              sx={{
                bgcolor: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: { xs: 2.5, md: 3 },
                px: { xs: 1, md: 1.2 },
                mb: { xs: 2, md: 2.4 },
              }}
            >
              <Tabs
                value={currentTabIndex}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="Navegacao do Agente de Contabilidade"
                sx={{
                  minHeight: 48,
                  "& .MuiTab-root": {
                    minHeight: 48,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 13,
                    color: colors.muted,
                  },
                }}
              >
                {navItems.map((item) => (
                  <Tab key={item.key} label={item.label} />
                ))}
              </Tabs>
            </Box>

            <PageHeader title={title} subtitle={subtitle} actions={actions} />
            <Box
              aria-busy={showUpdating ? "true" : "false"}
              sx={{ position: "relative", minHeight: showUpdating ? 180 : 0 }}
            >
              <Box
                sx={{
                  opacity: showUpdating ? 0.48 : 1,
                  transition: "opacity 160ms ease",
                  pointerEvents: showUpdating ? "none" : "auto",
                }}
              >
                {children}
              </Box>
              {showUpdating ? (
                <Box
                  role="status"
                  aria-live="polite"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 2,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    pt: { xs: 5, md: 7 },
                    bgcolor: "rgba(243, 247, 251, 0.34)",
                    cursor: "progress",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.2}
                    alignItems="center"
                    sx={{
                      px: 2,
                      py: 1.2,
                      bgcolor: colors.surface,
                      color: colors.text,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 2,
                      boxShadow: "0 10px 28px rgba(0, 20, 50, 0.14)",
                    }}
                  >
                    <CircularProgress size={21} />
                    <Typography sx={{ fontSize: 14, fontWeight: 800 }}>
                      Atualizando dados...
                    </Typography>
                  </Stack>
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>
      </Box>

      {shouldShowAgente && !aiOpen ? <AgenteFab onClick={() => setAgenteOpenState(true)} /> : null}

      {shouldShowAgente && isDesktop && aiOpen ? (
        <Box
          ref={widgetRef}
          sx={{
            position: "fixed",
            ...(widgetPosition
              ? { left: widgetPosition.left, top: widgetPosition.top }
              : { right: AGENTE_WIDGET_MARGIN, bottom: AGENTE_WIDGET_MARGIN }),
            width: 400,
            maxWidth: "calc(100vw - 56px)",
            height: "min(640px, calc(100vh - 96px))",
            zIndex: (theme) => theme.zIndex.drawer + 2,
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${colors.border}`,
            boxShadow: "0 22px 60px rgba(0,20,50,0.26)",
            transition: isDraggingAgente ? "none" : "box-shadow 0.18s ease",
          }}
        >
          <AgentePanel
            onClose={() => setAgenteOpenState(false)}
            docked
            context={agenteContext}
            dragHandleProps={{
              onPointerDown: handleAgenteDragStart,
              title: "Arraste para mover",
            }}
          />
        </Box>
      ) : null}

      <Drawer
        anchor="right"
        open={shouldShowAgente && !isDesktop && aiOpen}
        onClose={(_event, reason) => {
          if (shouldIgnoreOverlayClose(reason)) {
            return;
          }

          setAgenteOpenState(false);
        }}
        ModalProps={{ keepMounted: true, disableEscapeKeyDown: true }}
        PaperProps={{
          sx: {
            display: { md: "none" },
            width: { xs: "100%", sm: 390 },
            maxWidth: "100vw",
            bgcolor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <AgentePanel onClose={() => setAgenteOpenState(false)} context={agenteContext} />
      </Drawer>

      <TokenExpiredDialog
        open={showExpiredDialog}
        countdown={countdown}
        onRedirect={redirectToHome}
      />
    </Box>
  );
}
